import { BellOutlined, DeleteOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Select, Tag, Typography } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	ListPageIntro,
	ListPageShell,
	ListPageTotal,
	ModalCommon,
	PagedResourceList,
} from '../../common'
import { useAppDispatch, useAppSelector } from '../../hooks'
import {
	onDeleteNotificationAdmin,
	onDeleteManyNotificationsAdmin,
	onGetNotifications,
} from '../../redux/notification/notification.action'
import { DEFAULT_PAGE_SIZE } from '../../utils/constant'
import {
	formatDateTime,
	showMessageError,
	showMessageSuccess,
} from '../../utils/function.utils'
import './NotificationList.scss'

const decodeHtmlEntities = value =>
	value
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')

const toPlainText = value => {
	if (typeof value !== 'string') return ''
	return decodeHtmlEntities(value)
		.replace(/<[^>]*>/g, ' ')
		.replace(/\\n/g, '\n')
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.replace(/[ \t]{2,}/g, ' ')
		.trim()
}

const sanitizeHtml = value => {
	if (typeof value !== 'string' || value.trim() === '') return ''
	return value
		.replace(/<\s*(script|style|iframe|object|embed|link|meta)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
		.replace(/\son\w+="[^"]*"/gi, '')
		.replace(/\son\w+='[^']*'/gi, '')
		.replace(/javascript:/gi, '')
		.trim()
}

const parseMaybeJSON = value => {
	if (typeof value !== 'string') return value
	const trimmed = value.trim()
	if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value
	try {
		return JSON.parse(trimmed)
	} catch {
		return value
	}
}

const pickNotificationRawContent = data => {
	const payload = parseMaybeJSON(data)
	if (payload == null) return ''
	if (typeof payload === 'string') return payload
	if (typeof payload !== 'object') return String(payload)

	if (payload.content && typeof payload.content === 'object') {
		for (const key of ['default', 'vi', 'en']) {
			if (
				typeof payload.content[key] === 'string' &&
				payload.content[key].trim() !== ''
			) {
				return payload.content[key]
			}
		}
	}
	for (const key of ['content', 'message', 'title']) {
		if (typeof payload[key] === 'string' && payload[key].trim() !== '') {
			return payload[key]
		}
	}

	try {
		return JSON.stringify(payload, null, 2)
	} catch {
		return ''
	}
}

const getNotificationContent = data => {
	const raw = pickNotificationRawContent(data)
	return toPlainText(raw) || '—'
}

const getNotificationContentModalData = data => {
	const raw = pickNotificationRawContent(data)
	const payload = parseMaybeJSON(data)
	let json = ''
	if (payload != null && typeof payload === 'object') {
		try {
			json = JSON.stringify(payload, null, 2)
		} catch {
			json = ''
		}
	}
	return {
		plainText: toPlainText(raw) || '—',
		html: sanitizeHtml(raw),
		json,
	}
}

const NOTIFICATION_TYPE_OPTIONS = [
	'FRIEND_INVITATION',
	'ACCEPT_FRIEND_INVITATION',
	'REJECT_FRIEND_INVITATION',
	'COLLABORATIVE_FOLDER_INVITATION',
	'ACCEPT_COLLABORATIVE_FOLDER_INVITATION',
	'REJECT_COLLABORATIVE_FOLDER_INVITATION',
	'COLLABORATIVE_ROOM_INVITATION',
	'NOTIFICATION_TYPE_COLLABORATIVE_ROOM_START',
	'FEEDBACK_REPLY',
	'CUSTOM_NOTIFICATION',
	'NEW_COMMENT_USER_POST_FOR_AUTHOR',
	'NEW_COMMENT_USER_POST_FOR_TARGET_USER_POST',
	'NEW_USER_POST',
]

const toNotificationTypeLabel = value =>
	value
		.split('_')
		.map(chunk => chunk.charAt(0) + chunk.slice(1).toLowerCase())
		.join(' ')

export const NotificationList = () => {
	const dispatch = useAppDispatch()
	const notification = useAppSelector(state => state.notification)
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
	const [selectedTypes, setSelectedTypes] = useState([])
	const [deletingID, setDeletingID] = useState(null)
	const [selectedRowKeys, setSelectedRowKeys] = useState([])
	const [bulkDeleting, setBulkDeleting] = useState(false)
	const [isContentModalVisible, setIsContentModalVisible] = useState(false)
	const [selectedContentText, setSelectedContentText] = useState('')
	const [selectedContentHtml, setSelectedContentHtml] = useState('')
	const [selectedContentJson, setSelectedContentJson] = useState('')
	const [contentViewMode, setContentViewMode] = useState('text')
	const resource = useMemo(
		() => ({
			data: notification?.data || [],
			total: notification?.total || 0,
			page: notification?.page || 1,
		}),
		[notification]
	)
	const notificationTypeSelectOptions = useMemo(
		() =>
			NOTIFICATION_TYPE_OPTIONS.map(type => ({
				value: type,
				label: toNotificationTypeLabel(type),
			})),
		[]
	)

	const fetchPage = useCallback(
		(page = 0) =>
			dispatch(
				onGetNotifications({
					page,
					pageSize,
					types: selectedTypes,
				})
			),
		[dispatch, pageSize, selectedTypes]
	)

	const handleDelete = useCallback(
		record => {
			setDeletingID(record.id)
			dispatch(
				onDeleteNotificationAdmin({ id: record.id }, (isSuccess, rs) => {
					setDeletingID(null)
					if (!isSuccess) {
						showMessageError(rs)
						return
					}
					showMessageSuccess('Notification removed successfully.')
					const currentPageIndex = Math.max((resource.page || 1) - 1, 0)
					const shouldGoPreviousPage =
						(resource.data || []).length <= 1 && currentPageIndex > 0
					fetchPage(
						shouldGoPreviousPage ? currentPageIndex - 1 : currentPageIndex
					)
				})
			)
		},
		[dispatch, fetchPage, resource.data, resource.page]
	)

	const openContentModal = useCallback(data => {
		const content = getNotificationContentModalData(data)
		setSelectedContentText(content.plainText)
		setSelectedContentHtml(content.html)
		setSelectedContentJson(content.json)
		setContentViewMode('text')
		setIsContentModalVisible(true)
	}, [])

	const closeContentModal = useCallback(() => {
		setIsContentModalVisible(false)
		setSelectedContentText('')
		setSelectedContentHtml('')
		setSelectedContentJson('')
		setContentViewMode('text')
	}, [])

	const columns = useMemo(
		() => [
			{
				title: 'ID',
				dataIndex: 'id',
				key: 'id',
				width: 72,
			},
			{
				title: 'Type',
				dataIndex: 'type',
				key: 'type',
				width: 280,
				render: type => type || '—',
			},
			{
				title: 'Source user',
				dataIndex: 'sourceUser',
				key: 'sourceUser',
				width: 180,
				render: sourceUser => {
					if (!sourceUser) return '—'
					return (
						<span className="notification-list__source-user">
							{sourceUser.email || sourceUser.authName || sourceUser.id || '—'}
						</span>
					)
				},
			},
			{
				title: 'Read',
				dataIndex: 'isRead',
				key: 'isRead',
				width: 100,
				align: 'center',
				render: isRead =>
					isRead ? <Tag color="green">Yes</Tag> : <Tag color="orange">No</Tag>,
			},
			{
				title: 'Created at',
				dataIndex: 'createdAt',
				key: 'createdAt',
				width: 170,
				render: value => formatDateTime(value),
			},
			{
				title: 'Content',
				dataIndex: 'data',
				key: 'data',
				render: data => {
					const value = getNotificationContent(data)
					return (
						<button
							type="button"
							className="notification-list__content-trigger"
							onClick={() => openContentModal(data)}
						>
							<Typography.Paragraph ellipsis={{ rows: 2, expandable: false }}>
								{value}
							</Typography.Paragraph>
						</button>
					)
				},
			},
			{
				title: 'Actions',
				key: 'actions',
				width: 90,
				align: 'center',
				render: (_, record) => (
					<Popconfirm
						title="Delete this notification?"
						description="This removes it from user-facing pages."
						okText="Delete"
						cancelText="Cancel"
						okButtonProps={{
							danger: true,
							loading: deletingID === record.id,
						}}
						onConfirm={() => handleDelete(record)}
					>
						<Button
							type="text"
							danger
							icon={<DeleteOutlined />}
							loading={deletingID === record.id}
							className="notification-list__delete-btn"
						/>
					</Popconfirm>
				),
			},
		],
		[deletingID, handleDelete, openContentModal]
	)

	const handleBulkDelete = useCallback(() => {
		if (selectedRowKeys.length === 0) return
		setBulkDeleting(true)
		dispatch(
			onDeleteManyNotificationsAdmin(
				{ ids: selectedRowKeys },
				(isSuccess, rs) => {
					setBulkDeleting(false)
					if (!isSuccess) {
						showMessageError(rs)
						return
					}
					showMessageSuccess(
						`${selectedRowKeys.length} notification(s) removed successfully.`
					)
					setSelectedRowKeys([])
					const currentPageIndex = Math.max((resource.page || 1) - 1, 0)
					const shouldGoPreviousPage =
						(resource.data || []).length <= selectedRowKeys.length &&
						currentPageIndex > 0
					fetchPage(
						shouldGoPreviousPage ? currentPageIndex - 1 : currentPageIndex
					)
				}
			)
		)
	}, [dispatch, fetchPage, resource.data, resource.page, selectedRowKeys])

	const rowSelection = useMemo(
		() => ({
			selectedRowKeys,
			onChange: keys => setSelectedRowKeys(keys),
		}),
		[selectedRowKeys]
	)

	const currentPageRowKeys = useMemo(
		() =>
			(Array.isArray(resource.data) ? resource.data : [])
				.map(item => item.id)
				.filter(id => id != null),
		[resource.data]
	)

	const allCurrentPageSelected =
		currentPageRowKeys.length > 0 &&
		currentPageRowKeys.every(id => selectedRowKeys.includes(id))

	const toggleSelectCurrentPage = useCallback(() => {
		if (currentPageRowKeys.length === 0) return
		setSelectedRowKeys(prev => {
			if (allCurrentPageSelected) {
				return prev.filter(id => !currentPageRowKeys.includes(id))
			}
			const merged = new Set([...prev, ...currentPageRowKeys])
			return Array.from(merged)
		})
	}, [allCurrentPageSelected, currentPageRowKeys])

	useEffect(() => {
		dispatch(
			onGetNotifications({
				page: 0,
				pageSize,
				types: selectedTypes,
			})
		)
	}, [dispatch, pageSize, selectedTypes])

	useEffect(() => {
		const currentRows = Array.isArray(resource.data) ? resource.data : []
		const visibleIDs = new Set(currentRows.map(item => item.id))
		setSelectedRowKeys(prev => prev.filter(id => visibleIDs.has(id)))
	}, [resource.data])

	return (
		<>
			<ListPageShell>
				<PagedResourceList
					resource={resource}
					columns={columns}
					fetchPage={fetchPage}
					rowKey={record => record.id}
					pageSize={pageSize}
					onPageSizeChange={setPageSize}
					fetchOnMount={false}
					header={
						<div className="list-page-bar notification-list__bar">
							<ListPageIntro
								icon={BellOutlined}
								title="Notifications"
								subtitle="All notifications in database with paging support."
							/>
							<div className="list-page__aside">
								<div className="list-page__aside-row">
									<div className="notification-list__filters">
										<div className="notification-list__filters-label">
											Filter by notification type
										</div>
										<Select
											mode="multiple"
											allowClear
											showSearch
											maxTagCount="responsive"
											placeholder="Select one or more types"
											optionFilterProp="label"
											className="notification-list__filters-select"
											value={selectedTypes}
											onChange={setSelectedTypes}
											options={notificationTypeSelectOptions}
										/>
									</div>
									<ListPageTotal total={resource.total} />
								</div>
								<div className="list-page__aside-row notification-list__actions-row">
									<Button
										type="default"
										onClick={toggleSelectCurrentPage}
										disabled={currentPageRowKeys.length === 0}
										className="notification-list__select-page-btn"
									>
										{allCurrentPageSelected
											? 'Clear current page'
											: 'Select all items'}
									</Button>
									<Popconfirm
										title="Delete selected notifications?"
										description="This removes selected notifications from user-facing pages."
										okText="Delete selected"
										cancelText="Cancel"
										okButtonProps={{ danger: true, loading: bulkDeleting }}
										disabled={selectedRowKeys.length === 0}
										onConfirm={handleBulkDelete}
									>
										<Button
											danger
											type="default"
											icon={<DeleteOutlined />}
											disabled={selectedRowKeys.length === 0}
											loading={bulkDeleting}
											className="notification-list__bulk-delete-btn"
										>
											Delete selected ({selectedRowKeys.length})
										</Button>
									</Popconfirm>
								</div>
							</div>
						</div>
					}
					tableProps={{
						className: 'table-common--fluid notification-list__table',
						rowSelection,
					}}
				/>
			</ListPageShell>
			<ModalCommon
				title="Notification content"
				visible={isContentModalVisible}
				handleCancel={closeContentModal}
				width={760}
				footer={null}
			>
				<div className="notification-list__content-view-toggle">
					<Button
						size="small"
						type={contentViewMode === 'text' ? 'primary' : 'default'}
						onClick={() => setContentViewMode('text')}
					>
						Plain text
					</Button>
					<Button
						size="small"
						type={contentViewMode === 'html' ? 'primary' : 'default'}
						onClick={() => setContentViewMode('html')}
						disabled={!selectedContentHtml}
					>
						HTML preview
					</Button>
					<Button
						size="small"
						type={contentViewMode === 'json' ? 'primary' : 'default'}
						onClick={() => setContentViewMode('json')}
						disabled={!selectedContentJson}
					>
						Original JSON
					</Button>
				</div>
				<div className="notification-list__content-modal-body">
					{contentViewMode === 'json' && selectedContentJson ? (
						<pre className="notification-list__content-json-preview">
							{selectedContentJson}
						</pre>
					) : contentViewMode === 'html' && selectedContentHtml ? (
						<div
							className="notification-list__content-html-preview"
							dangerouslySetInnerHTML={{ __html: selectedContentHtml }}
						/>
					) : (
						selectedContentText || '—'
					)}
				</div>
			</ModalCommon>
		</>
	)
}
